import { useState } from 'react';

/** Strip non-digits and coerce to a non-negative integer; empty → 0. */
export function parseIntInput(raw: string): number {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? 0 : Math.floor(Number(digits));
}

/** What the input shows: the live draft while editing, else the committed value
 *  with 0 rendered as empty so the placeholder (e.g. "unlimited") is visible. */
export function displayInt(value: number, draft: string | null): string {
  return draft ?? (value === 0 ? '' : String(value));
}

interface Props {
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Non-negative integer input backed by a local draft buffer so the field can be
 * cleared and retyped freely. A controlled `<input type="number" value={n}>`
 * snaps straight back to "0" on every keystroke once the value hits zero, which
 * traps the cursor behind a leading zero — the buffer holds the raw text while
 * focused and only resyncs to the canonical value on blur.
 */
export function NumberField({ value, onChange, disabled, placeholder, className }: Props) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className={className}
      type="text"
      inputMode="numeric"
      disabled={disabled}
      placeholder={placeholder}
      value={displayInt(value, draft)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '');
        setDraft(digits);
        onChange(parseIntInput(digits));
      }}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={() => setDraft(null)}
    />
  );
}
